import { useTranslation } from "react-i18next";
import { CheckCircle2, Snowflake, TrendingDown } from "lucide-react";
import type { ProductSignalKind, ProductSignals } from "../api";
import { fmtMoney } from "../lib/format";
import { cn } from "../lib/utils";
import { InfoTooltip } from "./info-tooltip";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";

/**
 * Ombor signallari — Tahlil > Signallar tabining tepasida turadigan, ega
 * HECH NARSA bosmasdan ko'radigan ikki karta (bosilsa mos pill tanlanadi).
 *
 * Ataylab pul tilida: "3 ta tovar sekin" e'tiborsiz qoldiriladi, "3 ta tovarda
 * 4,2 mln qotib qolgan" esa yo'q. Chap karta — qotgan pul (tannarx bo'yicha,
 * ya'ni haqiqatan bog'lanib qolgan naqd), o'ng karta — tugayotgan tovarlar
 * (ular keltirgan tushum bilan: talab bor, zaxira esa qolmadi).
 *
 * Bo'sh holat ham GAP: bo'sh quti emas, "hammasi joyida" yoki "tarix hali
 * yetarli emas" degan jumla. Aks holda ega kartani buzuq deb o'ylardi.
 */
export function SignalCards({
  data,
  loading,
  onOpen,
}: {
  data: ProductSignals | null;
  loading: boolean;
  onOpen: (signal: ProductSignalKind) => void;
}) {
  const { t } = useTranslation("analytics");

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-[92px]" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  // Hech bir tovar baholanmagan — hukm chiqarish uchun tarix yetarli emas.
  // Bu XATO emas va nol ham emas, shuning uchun kartalar o'rniga bitta jumla.
  if (data.baholangan === 0) {
    return (
      <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
        {t("signallar.cards.noHistory", { days: data.window_days })}
      </div>
    );
  }

  const stuck = data.qotgan;
  const out = data.tugayotgan;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <SignalCard
        tone={stuck.soni > 0 ? "danger" : "ok"}
        icon={stuck.soni > 0 ? <Snowflake /> : <CheckCircle2 />}
        label={t("signallar.cards.stuck")}
        value={stuck.soni > 0 ? fmtMoney(stuck.qiymat) : t("signallar.cards.stuckNone")}
        sub={
          stuck.soni > 0
            ? t("signallar.cards.stuckSub", { count: stuck.soni })
            : t("signallar.cards.stuckNoneSub")
        }
        hint={
          stuck.narxsiz_soni > 0 ? (
            <InfoTooltip
              text={t("signallar.cards.noCostHint", { count: stuck.narxsiz_soni })}
            />
          ) : undefined
        }
        onClick={stuck.soni > 0 ? () => onOpen("qotgan") : undefined}
      />
      <SignalCard
        tone={out.soni > 0 ? "warn" : "ok"}
        icon={out.soni > 0 ? <TrendingDown /> : <CheckCircle2 />}
        label={t("signallar.cards.stockout")}
        value={
          out.soni > 0
            ? t("signallar.cards.stockoutValue", { count: out.soni })
            : t("signallar.cards.stockoutNone")
        }
        sub={
          out.soni > 0
            ? t("signallar.cards.stockoutSub", {
                amount: fmtMoney(out.sotuv_qiymati),
                days: data.window_days,
              })
            : t("signallar.cards.stockoutNoneSub")
        }
        onClick={out.soni > 0 ? () => onOpen("tugayotgan") : undefined}
      />
    </div>
  );
}

const TONE_STYLES = {
  danger: "border-destructive/40 bg-destructive/5 text-destructive",
  warn: "border-amber-500/40 bg-amber-500/5 text-amber-600 dark:text-amber-500",
  ok: "border-dashed text-muted-foreground",
} as const;

function SignalCard({
  tone,
  icon,
  label,
  value,
  sub,
  hint,
  onClick,
}: {
  tone: keyof typeof TONE_STYLES;
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  hint?: React.ReactNode;
  onClick?: () => void;
}) {
  // Muammo bo'lmasa karta bosilmaydi — ochadigan ro'yxati ham yo'q.
  const clickable = !!onClick;
  return (
    <Card
      className={cn(
        TONE_STYLES[tone],
        clickable && "cursor-pointer transition-colors hover:bg-muted/50",
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold">
          <span className="[&_svg]:size-3.5">{icon}</span>
          <span className="truncate">{label}</span>
          {hint}
        </div>
        <div className="font-num text-2xl font-bold tracking-tight tabular-nums mt-1 text-foreground">
          {value}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}
